import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { DataGrid, GridRowModes, GridActionsCellItem } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

import DataGridInfoAlert from "../../components/dataAdminGrid/InfoAlert";
import DeleteDialog from "../../components/dataAdminGrid/DeleteDialog";
import { ROUTES } from "../../constants";
import {
  WATER_SERVICE_CONNECTIONS_BASE_URL,
  COLUMNS as BASE_COLUMNS,
  WATER_SERVICE_CONNECTION_PK_FIELD,
} from "./waterServiceConnectionsConfig";

import AutocompleteEditCell from "../../components/dataAdminGrid/AutocompleteEditCell";
import { useWaterServiceConnectionLookups } from "./useWaterServiceConnectionLookups";
import WaterServiceConnectionsToolbar from "./WaterServiceConnectionsToolbar";
import { useApp } from "../../AppProvider";
import { generateTempRowId } from "../../components/dataAdminGrid/utils";

const StyledDataGrid = styled(DataGrid)({
  height: 600,
  "& .MuiDataGrid-row:hover": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "#d9e7f5",
  },
  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "#c3dbf2",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing .MuiDataGrid-cell": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing:hover .MuiDataGrid-cell": {
    backgroundColor: "#ffeeba",
  },
});

function WaterServiceConnectionsPage() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { doToast } = useApp();
  const { pwsOperators } = useWaterServiceConnectionLookups();
  const [showDeniedRecords, setShowDeniedRecords] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});
  const [localRows, setLocalRows] = useState([]);

  // Fetch all water service connection records
  const { data: allRecords = [], isLoading } = useQuery(
    ["water-service-connections"],
    async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(WATER_SERVICE_CONNECTIONS_BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.map((record) => ({
        ...record,
        id: record.pws_connection_ndx, // Add id field for DataGrid
      }));
    }
  );

  // Combine server data with local rows and filter
  const allRows = useMemo(() => {
    const serverRows = allRecords.map((record) => ({
      ...record,
      rowId: record.id,
    }));
    return [...localRows, ...serverRows];
  }, [allRecords, localRows]);

  // Filter records based on showDeniedRecords state
  const filteredRecords = useMemo(() => {
    if (showDeniedRecords) {
      return allRows; // Show all records including denied
    }
    // Show only approved records and new local rows (exclude denied where connection_approved is false)
    return allRows.filter((record) => record.connection_approved !== false);
  }, [allRows, showDeniedRecords]);

  // Delete mutation
  const deleteMutation = useMutation(
    async (recordId) => {
      const token = await getAccessTokenSilently();
      await axios.delete(`${WATER_SERVICE_CONNECTIONS_BASE_URL}/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries("water-service-connections");
        doToast("success", "Record deleted successfully");
      },
      onError: () => {
        doToast("error", "Failed to delete record");
      },
    }
  );

  // Save mutation (handles both create and update)
  const saveMutation = useMutation(
    async (record) => {
      const token = await getAccessTokenSilently();
      const payload = { ...record };
      delete payload.rowId;
      delete payload.id;

      const response = await axios.put(
        WATER_SERVICE_CONNECTIONS_BASE_URL,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries("water-service-connections");
        doToast("success", "Record saved successfully");
      },
      onError: () => {
        doToast("error", "Failed to save record");
      },
    }
  );

  const handleEditClick = useCallback(
    (id) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }));
    },
    []
  );

  const handleSaveClick = useCallback(
    (id) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }));
    },
    []
  );

  const handleDeleteClick = useCallback(
    (id) => () => {
      const record = allRows.find((r) => r.rowId === id);
      const pkValue = record[WATER_SERVICE_CONNECTION_PK_FIELD];
      if (pkValue == null) {
        // Remove local row immediately
        setLocalRows((prev) => prev.filter((r) => r.rowId !== id));
      } else {
        setRecordToDelete(record);
        setDeleteDialogOpen(true);
      }
    },
    [allRows]
  );

  const handleCancelClick = useCallback(
    (id) => () => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: {
          mode: GridRowModes.View,
          ignoreModifications: true,
        },
      }));
      // Remove new local rows when cancelled
      setLocalRows((prev) => prev.filter((r) => r.rowId !== id));
    },
    []
  );

  const handleConfirmDelete = async () => {
    if (recordToDelete) {
      await deleteMutation.mutateAsync(recordToDelete.pws_connection_ndx);
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    }
  };

  const handleAddNewRecord = () => {
    const newRowId = generateTempRowId();
    const newRow = {
      rowId: newRowId,
      id: newRowId,
      [WATER_SERVICE_CONNECTION_PK_FIELD]: null,
      connection_approved: true, // Default to approved for new records
    };
    setLocalRows([newRow, ...localRows]);
    setRowModesModel((prev) => ({
      ...prev,
      [newRowId]: { mode: GridRowModes.Edit, fieldToFocus: "first_name" },
    }));
  };

  const processRowUpdate = useCallback(
    async (updatedRow) => {
      try {
        const savedRow = await saveMutation.mutateAsync(updatedRow);
        // Remove from local rows and let server data take over
        setLocalRows((prev) =>
          prev.filter((r) => r.rowId !== updatedRow.rowId)
        );
        return { ...updatedRow, ...savedRow };
      } catch (error) {
        doToast("error", "Failed to save record");
        return updatedRow;
      }
    },
    [saveMutation, doToast]
  );

  const columns = useMemo(() => {
    const actionsColumn = {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      cellClassName: "actions",
      getActions: ({ row }) => {
        const rowId = row.rowId;
        const isInEditMode = rowModesModel[rowId]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(rowId)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(rowId)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(rowId)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(rowId)}
            color="inherit"
          />,
        ];
      },
    };

    const dataColumns = BASE_COLUMNS.map((col) => {
      if (col.field === "pws") {
        return {
          ...col,
          renderCell: ({ value }) => {
            const found = pwsOperators.find(
              (z) => z.access_contact_ndx === value
            );
            return found ? (
              found.contact
            ) : (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No PWS Operator Selected
              </Typography>
            );
          },
          renderEditCell: (params) => (
            <AutocompleteEditCell
              {...params}
              options={pwsOperators}
              optionIdField="access_contact_ndx"
              optionLabelField="contact"
              multiple={false}
              label="PWS Operator"
            />
          ),
        };
      }
      return col;
    });

    return [actionsColumn, ...dataColumns];
  }, [
    pwsOperators,
    rowModesModel,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
  ]);

  return (
    <Box>
      <Helmet title="Water Service Connections Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Water Service Connections
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Water Service Connections</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <DataGridInfoAlert title="Manage water service connections:" />

      <StyledDataGrid
        rows={filteredRecords}
        columns={columns}
        loading={isLoading}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        getRowId={(row) => row.rowId}
        slots={{
          toolbar: WaterServiceConnectionsToolbar,
        }}
        slotProps={{
          toolbar: {
            showPendingRecords: showDeniedRecords,
            onShowPendingRecordsChange: setShowDeniedRecords,
            onAddNewRecord: handleAddNewRecord,
          },
        }}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete this water service connection record?`}
      />
    </Box>
  );
}

export default WaterServiceConnectionsPage;
